<template>
  <v-app>
    <v-container
      fluid
      class="pa-4 pa-md-8"
      style="max-width: 900px; margin: 0 auto;"
    >
      <v-row
        justify="center"
        class="mb-6"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <img
            src="@/assets/images/logos/chess-hotel.png"
            alt="CHESS HOTEL"
            style="max-width: 320px;"
          >
        </v-col>
      </v-row>

      <v-stepper
        v-model="step"
        elevation="4"
      >
        <v-stepper-header>
          <v-stepper-step
            :complete="step > 1"
            step="1"
          >
            Reserva
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step
            :complete="step > 2"
            step="2"
          >
            Hóspedes
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3">
            Confirmação
          </v-stepper-step>
        </v-stepper-header>

        <!-- ── STEP 1: Número da reserva ─────────────────────── -->
        <v-stepper-content step="1">
          <v-card
            flat
            class="pa-4"
          >
            <v-card-title class="justify-center text-h6 mb-2">
              Pré Check-in Online
            </v-card-title>
            <v-card-subtitle class="text-center mb-4">
              Informe o número da sua reserva para iniciar o pré check-in.
            </v-card-subtitle>
            <v-row justify="center">
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="reserveId"
                  outlined
                  label="Número da Reserva"
                  type="number"
                  :error-messages="reserveError"
                  @input="reserveError = ''"
                  @keyup.enter="loadReserve"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  x-large
                  :loading="loadingReserve"
                  @click="loadReserve"
                >
                  Continuar
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-stepper-content>

        <!-- ── STEP 2: Hóspedes ──────────────────────────────── -->
        <v-stepper-content step="2">
          <v-card
            v-if="reserve"
            flat
            class="pa-2 pa-md-4"
          >
            <v-alert
              type="info"
              outlined
              class="mb-4"
              dense
            >
              <strong>Reserva #{{ reserve.id }}</strong> —
              Check-in: {{ formatDate(reserve.check_in) }} |
              Check-out: {{ formatDate(reserve.check_out) }}
              <span v-if="reserve.category"> | {{ reserve.category }}</span>
            </v-alert>

            <v-alert
              v-if="reserve.pre_checkin_done"
              type="success"
              outlined
              dense
              class="mb-4"
              icon="mdi-check-circle"
            >
              <strong>Pré Check-in já realizado!</strong>
              Os dados dos hóspedes desta reserva já foram registrados.
              Você pode atualizar as informações abaixo se necessário.
            </v-alert>

            <!-- Regras do Hotel -->
            <div
              v-if="reserve.rules && reserve.rules.length"
              class="mb-4"
            >
              <div class="d-flex align-center mb-2">
                <v-icon
                  color="orange darken-2"
                  class="mr-2"
                >
                  mdi-shield-alert-outline
                </v-icon>
                <span class="subtitle-2 font-weight-bold">Regras do Hotel</span>
              </div>
              <div
                v-for="(rule, i) in reserve.rules"
                :key="i"
                class="rule-card mb-3 pa-3 d-flex align-start"
                :class="rule.icon === 'smoking' ? 'rule-smoking' : 'rule-info'"
              >
                <div class="rule-icon-wrap mr-3 flex-shrink-0">
                  <v-icon
                    size="28"
                    :color="rule.icon === 'smoking' ? 'red darken-2' : 'blue darken-1'"
                  >
                    {{ rule.icon === 'smoking' ? 'mdi-smoking-off' : 'mdi-information' }}
                  </v-icon>
                </div>
                <div
                  class="body-2"
                  style="line-height:1.5;"
                  v-html="nl2br(rule.text)"
                ></div>
              </div>
            </div>

            <!-- Tabs por hóspede -->
            <v-tabs
              v-model="activeTab"
              show-arrows
              class="mb-1"
            >
              <v-tab
                v-for="(guest, i) in guests"
                :key="i"
              >
                <v-icon
                  small
                  class="mr-1"
                >
                  {{ guest.type === 'child' ? 'mdi-account-child' : 'mdi-account' }}
                </v-icon>
                {{ guestLabel(guest, i) }}
              </v-tab>
            </v-tabs>
            <v-divider class="mb-4"></v-divider>

            <!-- Formulário do hóspede ativo -->
            <v-tabs-items v-model="activeTab">
              <v-tab-item
                v-for="(guest, tabIdx) in guests"
                :key="tabIdx"
              >
                <div class="mb-4">
                  <v-chip
                    v-if="guest.isMain"
                    color="primary"
                    outlined
                    small
                  >
                    <v-icon
                      left
                      x-small
                    >
                      mdi-star
                    </v-icon>Principal — obrigatório para NF-e
                  </v-chip>
                  <v-chip
                    v-else-if="guest.type === 'adult'"
                    color="grey darken-1"
                    outlined
                    small
                  >
                    Adulto adicional
                  </v-chip>
                  <v-chip
                    v-else
                    color="teal"
                    outlined
                    small
                  >
                    Criança
                  </v-chip>
                </div>

                <!-- Tipo + Documento + Nome -->
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    md="3"
                    class="pr-md-2"
                  >
                    <v-select
                      :value="guest.type_doc"
                      :items="guest.isMain ? mainDocTypes : typeDocItems"
                      outlined
                      :label="fl(guest, 'type_doc', 'Tipo de documento')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].type_doc"
                      @input="val => { setGuest(tabIdx, 'type_doc', val); setGuest(tabIdx, 'cpf', ''); setGuest(tabIdx, 'doc', ''); setGuest(tabIdx, 'foreigner', val !== 'CPF') }"
                      @focus="clearError(tabIdx, 'type_doc')"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    class="px-md-2"
                  >
                    <v-text-field
                      v-if="guest.type_doc === 'CPF' || !guest.type_doc"
                      v-mask="'###.###.###-##'"
                      :value="guest.cpf"
                      outlined
                      :label="guest.type === 'child' ? 'CPF (opcional)' : fl(guest, 'cpf', 'CPF')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cpf"
                      @input="val => { setGuest(tabIdx, 'cpf', val); onCpfChange(tabIdx, val) }"
                      @focus="clearError(tabIdx, 'cpf')"
                    ></v-text-field>
                    <v-text-field
                      v-else
                      :value="guest.doc"
                      outlined
                      :label="fl(guest, 'doc', 'Número do documento')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].doc"
                      @input="val => setGuest(tabIdx, 'doc', val)"
                      @focus="clearError(tabIdx, 'doc')"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="5"
                  >
                    <v-text-field
                      :value="guest.name"
                      outlined
                      :label="fl(guest, 'name', 'Nome completo')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].name"
                      @input="val => setGuestName(tabIdx, val)"
                      @focus="clearError(tabIdx, 'name')"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Data nascimento + Sexo + Nacionalidade -->
                <v-row
                  no-gutters
                  class="mt-n2"
                >
                  <v-col
                    cols="12"
                    md="4"
                    class="pr-md-2"
                  >
                    <v-text-field
                      :value="guest.date_of_birth"
                      outlined
                      :label="fl(guest, 'date_of_birth', 'Data de nascimento')"
                      type="date"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].date_of_birth"
                      @input="val => setGuest(tabIdx, 'date_of_birth', val)"
                      @focus="clearError(tabIdx, 'date_of_birth')"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    class="px-md-2"
                  >
                    <v-select
                      :value="guest.gender"
                      :items="genderItems"
                      item-text="text"
                      item-value="value"
                      outlined
                      :label="fl(guest, 'gender', 'Sexo')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].gender"
                      @input="val => setGuest(tabIdx, 'gender', val)"
                      @focus="clearError(tabIdx, 'gender')"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      :value="guest.nationality_country_id"
                      :items="countryItems"
                      item-value="id"
                      item-text="name"
                      outlined
                      :label="fl(guest, 'nationality_country_id', 'Nacionalidade')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].nationality_country_id"
                      @input="val => setGuest(tabIdx, 'nationality_country_id', val)"
                      @focus="clearError(tabIdx, 'nationality_country_id')"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Campos para todos os adultos -->
                <template v-if="guest.type === 'adult'">
                  <v-row
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="4"
                      class="pr-md-2"
                    >
                      <v-select
                        :value="guest.marital_status"
                        :items="maritalItems"
                        outlined
                        :label="fl(guest, 'marital_status', 'Estado civil')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].marital_status"
                        @input="val => setGuest(tabIdx, 'marital_status', val)"
                        @focus="clearError(tabIdx, 'marital_status')"
                      ></v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      class="px-md-2"
                    >
                      <v-text-field
                        :value="guest.profession"
                        outlined
                        :label="fl(guest, 'profession', 'Profissão')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].profession"
                        @input="val => setGuest(tabIdx, 'profession', val)"
                        @focus="clearError(tabIdx, 'profession')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                    >
                      <v-text-field
                        :value="guest.origin"
                        outlined
                        :label="fl(guest, 'origin', 'Cidade de origem')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].origin"
                        @input="val => setGuest(tabIdx, 'origin', val)"
                        @focus="clearError(tabIdx, 'origin')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="guest.type_doc === 'RG'"
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="6"
                      class="pr-md-2"
                    >
                      <v-text-field
                        :value="guest.org_exp"
                        outlined
                        label="Órgão expedidor *"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].org_exp"
                        @input="val => setGuest(tabIdx, 'org_exp', val)"
                        @focus="clearError(tabIdx, 'org_exp')"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="my-3"></v-divider>
                  <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                    Endereço
                  </div>
                  <v-row no-gutters>
                    <v-col
                      cols="12"
                      md="3"
                      class="pr-md-2"
                    >
                      <v-text-field
                        v-if="guest.foreigner"
                        :value="guest.zipcode"
                        outlined
                        :label="fl(guest, 'zipcode', 'CEP / Postal Code')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].zipcode"
                        @input="val => setGuest(tabIdx, 'zipcode', val)"
                        @focus="clearError(tabIdx, 'zipcode')"
                      ></v-text-field>
                      <v-text-field
                        v-else
                        v-mask="'#####-###'"
                        :value="guest.zipcode"
                        outlined
                        :label="fl(guest, 'zipcode', 'CEP')"
                        :loading="loadingCep[tabIdx]"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].zipcode"
                        @input="val => { setGuest(tabIdx, 'zipcode', val); getCEP(tabIdx, val) }"
                        @focus="clearError(tabIdx, 'zipcode')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="7"
                      class="px-md-2"
                    >
                      <v-text-field
                        :value="guest.address"
                        outlined
                        :label="fl(guest, 'address', 'Endereço')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].address"
                        @input="val => setGuest(tabIdx, 'address', val)"
                        @focus="clearError(tabIdx, 'address')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="2"
                    >
                      <v-text-field
                        :value="guest.number"
                        outlined
                        :label="fl(guest, 'number', 'Número')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].number"
                        @input="val => setGuest(tabIdx, 'number', val)"
                        @focus="clearError(tabIdx, 'number')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="4"
                      class="pr-md-2"
                    >
                      <v-text-field
                        :value="guest.complement"
                        outlined
                        label="Complemento"
                        hide-details
                        @input="val => setGuest(tabIdx, 'complement', val)"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="8"
                    >
                      <v-text-field
                        :value="guest.neighborhood"
                        outlined
                        :label="fl(guest, 'neighborhood', 'Bairro')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].neighborhood"
                        @input="val => setGuest(tabIdx, 'neighborhood', val)"
                        @focus="clearError(tabIdx, 'neighborhood')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="3"
                      class="pr-md-2"
                    >
                      <v-select
                        :value="guest.country_id"
                        :items="countryItems"
                        item-value="id"
                        item-text="name"
                        outlined
                        :label="fl(guest, 'country_id', 'País')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].country_id"
                        @input="val => setGuest(tabIdx, 'country_id', val)"
                        @focus="clearError(tabIdx, 'country_id')"
                      ></v-select>
                    </v-col>
                    <v-col
                      v-if="!guest.foreigner"
                      cols="12"
                      md="4"
                      class="px-md-2"
                    >
                      <v-select
                        :value="guest.state_id"
                        :items="stateItems"
                        item-value="id"
                        item-text="name"
                        outlined
                        :label="fl(guest, 'state_id', 'Estado')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].state_id"
                        @input="val => onStateChange(tabIdx, val)"
                        @focus="clearError(tabIdx, 'state_id')"
                      ></v-select>
                    </v-col>
                    <v-col
                      v-if="!guest.foreigner"
                      cols="12"
                      md="5"
                    >
                      <v-select
                        :value="guest.city_id"
                        :items="cityItems[tabIdx] || []"
                        item-value="id"
                        item-text="name"
                        outlined
                        :label="fl(guest, 'city_id', 'Cidade')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].city_id"
                        @input="val => setGuest(tabIdx, 'city_id', val)"
                        @focus="clearError(tabIdx, 'city_id')"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-divider class="my-3"></v-divider>
                  <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                    Contato
                  </div>
                  <v-row no-gutters>
                    <v-col
                      cols="12"
                      md="6"
                      class="pr-md-2"
                    >
                      <v-text-field
                        :value="guest.email"
                        outlined
                        :label="fl(guest, 'email', 'E-mail')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].email"
                        @input="val => setGuest(tabIdx, 'email', val.toLowerCase())"
                        @focus="clearError(tabIdx, 'email')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        v-if="guest.foreigner"
                        :value="guest.cell_phone"
                        outlined
                        :label="fl(guest, 'cell_phone', 'Celular')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cell_phone"
                        @input="val => setGuest(tabIdx, 'cell_phone', val)"
                        @focus="clearError(tabIdx, 'cell_phone')"
                      ></v-text-field>
                      <v-text-field
                        v-else
                        v-mask="'(##)#####-####'"
                        :value="guest.cell_phone"
                        outlined
                        :label="fl(guest, 'cell_phone', 'Celular')"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cell_phone"
                        @input="val => setGuest(tabIdx, 'cell_phone', val)"
                        @focus="clearError(tabIdx, 'cell_phone')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>

                <!-- Dados FNRH (todos os hóspedes) -->
                <v-divider class="my-3"></v-divider>
                <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                  Dados FNRH
                </div>
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    md="6"
                    class="pr-md-2"
                  >
                    <v-select
                      :value="guest.race"
                      :items="raceItems"
                      outlined
                      :label="fl(guest, 'race', 'Raça/Cor')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].race"
                      @input="val => setGuest(tabIdx, 'race', val)"
                      @focus="clearError(tabIdx, 'race')"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-select
                      :value="guest.disability_option"
                      :items="disabilityOptions"
                      outlined
                      :label="fl(guest, 'disability_option', 'Deficiência')"
                      :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].disability_option"
                      @input="val => setGuest(tabIdx, 'disability_option', val)"
                      @focus="clearError(tabIdx, 'disability_option')"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row
                  v-if="guest.disability_option && guest.disability_option !== 'Nenhuma'"
                  no-gutters
                  class="mt-n2"
                >
                  <v-col cols="12">
                    <v-text-field
                      :value="guest.disability_type"
                      outlined
                      label="Descrição da deficiência"
                      @input="val => setGuest(tabIdx, 'disability_type', val)"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>

            <!-- Navegação -->
            <v-row class="mt-4">
              <v-col>
                <v-btn
                  text
                  @click="prevTab"
                >
                  {{ activeTab === 0 ? 'Voltar' : '← Anterior' }}
                </v-btn>
              </v-col>
              <v-col class="text-right">
                <v-btn
                  v-if="activeTab < guests.length - 1"
                  color="primary"
                  large
                  @click="nextTab"
                >
                  Próximo →
                </v-btn>
                <v-btn
                  v-else
                  color="primary"
                  x-large
                  :loading="loadingSubmit"
                  @click="submitPreCheckIn"
                >
                  Confirmar Pré Check-in
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-stepper-content>

        <!-- ── STEP 3: Sucesso ───────────────────────────────── -->
        <v-stepper-content step="3">
          <v-card
            flat
            class="pa-6 text-center"
          >
            <v-icon
              color="success"
              size="80"
              class="mb-4"
            >
              mdi-check-circle-outline
            </v-icon>
            <div class="text-h5 mb-2">
              Pré Check-in Realizado!
            </div>
            <div class="text-body-1 grey--text mb-6">
              Os hóspedes foram vinculados à reserva <strong>#{{ reserve ? reserve.id : '' }}</strong>.
              Na chegada ao hotel, o check-in será mais rápido pois os dados já estão registrados.
            </div>
            <v-divider class="mb-4"></v-divider>
            <div class="text-left mb-2">
              <strong>Hóspedes registrados:</strong>
            </div>
            <v-list dense>
              <v-list-item
                v-for="(g, i) in guests"
                :key="i"
              >
                <v-list-item-icon>
                  <v-icon :color="g.type === 'child' ? 'teal' : 'primary'">
                    {{ g.type === 'child' ? 'mdi-account-child' : 'mdi-account' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ g.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="g.isMain">
                    Hóspede Principal
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-stepper-content>
      </v-stepper>
    </v-container>
  </v-app>
</template>

<script>
import { mapActions } from 'vuex'
import axios from 'axios'

const REQUIRED_MAIN = [
  'name', 'cpf', 'date_of_birth', 'gender', 'nationality_country_id',
  'marital_status', 'profession', 'origin', 'type_doc',
  'address', 'number', 'neighborhood', 'zipcode', 'country_id', 'state_id', 'city_id',
  'email', 'cell_phone', 'race', 'disability_option',
]
const REQUIRED_FNRH = ['name', 'cpf', 'date_of_birth', 'gender', 'nationality_country_id', 'race', 'disability_option']
const REQUIRED_CHILD = ['name', 'date_of_birth', 'gender', 'race', 'disability_option']

const FIELD_LABELS = {
  name: 'Nome',
  cpf: 'CPF',
  date_of_birth: 'Data de nascimento',
  gender: 'Sexo',
  nationality_country_id: 'Nacionalidade',
  marital_status: 'Estado civil',
  profession: 'Profissão',
  origin: 'Cidade de origem',
  doc: 'Documento',
  type_doc: 'Tipo de documento',
  org_exp: 'Órgão expedidor',
  address: 'Endereço',
  number: 'Número',
  neighborhood: 'Bairro',
  zipcode: 'CEP',
  country_id: 'País',
  state_id: 'Estado',
  city_id: 'Cidade',
  email: 'E-mail',
  cell_phone: 'Celular',
  race: 'Raça/Cor',
  disability_option: 'Deficiência',
}

export default {
  name: 'PreCheckIn',

  data: () => ({
    step: 1,
    reserveId: '',
    reserveError: '',
    reserve: null,
    guests: [],
    guestErrors: [],
    activeTab: 0,
    cityItems: {},
    loadingCep: {},
    countryItems: [],
    stateItems: [],
    loadingReserve: false,
    loadingSubmit: false,

    genderItems: [{ text: 'Masculino', value: 'M' }, { text: 'Feminino', value: 'F' }],
    maritalItems: ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'],
    mainDocTypes: ['CPF', 'Passaporte', 'RNE'],
    typeDocItems: ['CPF', 'RG', 'CNH', 'Passaporte', 'RNE', 'Certidão de Nascimento', 'Outro'],
    raceItems: ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não Declarada'],
    disabilityOptions: ['Nenhuma', 'Física', 'Visual', 'Auditiva', 'Intelectual', 'Múltipla'],
  }),

  created() {
    this.$store.dispatch('noauth/get_countries').then(r => { this.countryItems = r.data })
    this.$store.dispatch('noauth/get_states').then(r => { this.stateItems = r.data })
  },

  methods: {
    ...mapActions('noauth', ['get_reserve_info', 'submit_precheckin', 'isRegistered']),

    formatDate(date) {
      if (!date) return ''
      const [y, m, d] = date.split('-')

      return `${d}/${m}/${y}`
    },

    nl2br(text) {
      if (!text) return ''

      return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    },

    guestLabel(guest, i) {
      if (guest.isMain) return 'Principal'
      if (guest.type === 'child') {
        const n = this.guests.slice(0, i + 1).filter(g => g.type === 'child').length

        return `Criança ${n}`
      }
      const n = this.guests.slice(0, i + 1).filter(g => g.type === 'adult').length

      return `Adulto ${n}`
    },

    emptyGuest() {
      return {
        id: null,
        name: '',
        cpf: '',
        date_of_birth: '',
        gender: '',
        nationality_country_id: '',
        marital_status: '',
        profession: '',
        origin: '',
        doc: '',
        type_doc: 'CPF',
        org_exp: '',
        address: '',
        number: '',
        complement: '',
        neighborhood: '',
        zipcode: '',
        country_id: '',
        state_id: '',
        city_id: '',
        email: '',
        cell_phone: '',
        race: '',
        disability_option: '',
        disability_type: '',
        foreigner: false,
      }
    },

    loadReserve() {
      if (!this.reserveId) {
        this.reserveError = 'Informe o número da reserva'

        return
      }
      this.loadingReserve = true
      this.get_reserve_info({ reserve_id: this.reserveId })
        .then(({ data }) => {
          if (!data.status) {
            this.reserveError = data.message

            return
          }
          this.reserve = data.data
          this.initForms(data.data)
          this.step = 2
        })
        .catch(err => {
          this.reserveError = err.response?.data?.message || 'Reserva não encontrada ou não confirmada.'
        })
        .finally(() => { this.loadingReserve = false })
    },

    initForms(reserve) {
      const existing = reserve.guests || []
      const unknowns = reserve.guest_unknowns || []
      const merged = [...existing, ...unknowns.map(u => ({ name: u.name }))]
      const adultCount = reserve.adult_count || 1
      const childCount = reserve.children_count || 0

      const adults = Array.from({ length: adultCount }, (_, i) => ({
        type: 'adult',
        isMain: i === 0,
        incomplete_registration: i !== 0,
        ...this.emptyGuest(),
        ...(merged[i] || {}),
      }))

      const children = Array.from({ length: childCount }, (_, i) => ({
        type: 'child',
        isMain: false,
        incomplete_registration: true,
        ...this.emptyGuest(),
        ...(merged[adultCount + i] || {}),
      }))

      this.guests = [...adults, ...children]
      this.guestErrors = this.guests.map(() => ({}))
      this.cityItems = {}
      this.loadingCep = {}
      this.activeTab = 0

      this.guests.forEach((g, i) => {
        if (g.state_id) this.loadCities(i, g.state_id)
      })
    },

    setGuest(idx, key, val) {
      this.$set(this.guests, idx, { ...this.guests[idx], [key]: val })
    },

    setGuestName(idx, val) {
      const formatted = val.split(' ').map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ')
      this.setGuest(idx, 'name', formatted)
    },

    clearError(idx, key) {
      const errs = { ...this.guestErrors[idx] }
      delete errs[key]
      this.$set(this.guestErrors, idx, errs)
    },

    loadCities(guestIdx, stateId) {
      const state = this.stateItems.find(s => s.id === stateId)
      if (!state) return
      this.$store.dispatch('noauth/get_cities', state.code).then(r => {
        this.$set(this.cityItems, guestIdx, r.data)
      })
    },

    onStateChange(idx, stateId) {
      this.setGuest(idx, 'state_id', stateId)
      this.setGuest(idx, 'city_id', '')
      this.$set(this.cityItems, idx, [])
      this.loadCities(idx, stateId)
    },

    getCEP(idx, zipcode) {
      const raw = (zipcode || '').replace(/\D/g, '')
      if (raw.length !== 8) return
      this.$set(this.loadingCep, idx, true)
      axios.get(`https://viacep.com.br/ws/${raw}/json/`)
        .then(({ data }) => {
          if (data.erro) return
          this.setGuest(idx, 'address', data.logradouro)
          this.setGuest(idx, 'neighborhood', data.bairro)
          this.setGuest(idx, 'country_id', 31)
          const state = this.stateItems.find(s => s.acronym === data.uf)
          if (state) {
            this.setGuest(idx, 'state_id', state.id)
            this.$store.dispatch('noauth/get_cities', state.code).then(r => {
              this.$set(this.cityItems, idx, r.data)
              const city = r.data.find(c => c.name === data.localidade)
              if (city) this.setGuest(idx, 'city_id', city.id)
            })
          }
        })
        .catch(() => {})
        .finally(() => { this.$set(this.loadingCep, idx, false) })
    },

    onCpfChange(idx, cpf) {
      const raw = (cpf || '').replace(/\D/g, '')
      if (raw.length !== 11) return
      this.isRegistered({ cpf }).then(({ data }) => {
        if (data.status && data.data) {
          const current = this.guests[idx]
          this.$set(this.guests, idx, {
            ...current,
            ...data.data,
            type_doc: current.type_doc || 'CPF',
            isMain: current.isMain,
            type: current.type,
            incomplete_registration: !current.isMain,
          })
          if (data.data.state_id) this.loadCities(idx, data.data.state_id)
        }
      })
    },

    requiredFields(guest) {
      const useCpf = !guest.type_doc || guest.type_doc === 'CPF'
      const needOrg = guest.type_doc === 'RG'
      const swap = fields => {
        let result = fields.map(f => (f === 'cpf' ? (useCpf ? 'cpf' : 'doc') : f))
        if (needOrg && !result.includes('org_exp')) result.push('org_exp')
        if (guest.foreigner) result = result.filter(f => !['state_id', 'city_id'].includes(f))
        else result = result.filter(f => f !== 'nationality_country_id')

        return result
      }
      if (guest.isMain) return swap(REQUIRED_MAIN)
      if (guest.type === 'child') return REQUIRED_CHILD

      return swap(REQUIRED_FNRH)
    },

    // Retorna o label com '*' se o campo for obrigatório para o hóspede
    fl(guest, field, label) {
      return this.requiredFields(guest).includes(field) ? `${label} *` : label
    },

    validateGuest(tabIdx) {
      const guest = this.guests[tabIdx]
      const required = this.requiredFields(guest)
      const errs = {}
      required.forEach(f => {
        if (!guest[f]) errs[f] = `${FIELD_LABELS[f]} é obrigatório`
      })
      this.$set(this.guestErrors, tabIdx, errs)

      return Object.keys(errs).length === 0
    },

    nextTab() {
      if (this.validateGuest(this.activeTab)) {
        this.activeTab++
      } else {
        this.$toast.error('Preencha todos os campos obrigatórios.')
      }
    },

    prevTab() {
      if (this.activeTab === 0) {
        this.step = 1
      } else {
        this.activeTab--
      }
    },

    submitPreCheckIn() {
      const results = this.guests.map((_, i) => this.validateGuest(i))
      if (results.some(v => !v)) {
        this.activeTab = results.findIndex(v => !v)
        this.$toast.error('Preencha todos os campos obrigatórios.')

        return
      }
      const payload = this.guests.map(({ isMain, ...g }) => ({
        ...g,
        incomplete_registration: !isMain,
      }))
      this.loadingSubmit = true
      this.submit_precheckin({ reserve_id: this.reserve.id, guests: payload })
        .then(({ data }) => {
          if (data.status) {
            this.step = 3
          } else {
            this.$toast.error(data.message || 'Erro ao realizar pré check-in.')
          }
        })
        .catch(err => {
          this.$toast.error(err.response?.data?.message || 'Erro ao realizar pré check-in.')
        })
        .finally(() => { this.loadingSubmit = false })
    },
  },
}
</script>

<style scoped>
.v-stepper__content {
  padding: 16px 16px 8px;
}

.rule-card {
  border-radius: 8px;
  border-left: 4px solid;
}

.rule-smoking {
  background-color: #fff5f5;
  border-color: #e53935;
}

.rule-info {
  background-color: #f0f7ff;
  border-color: #1e88e5;
}

.rule-icon-wrap {
  line-height: 1;
  padding-top: 2px;
}
</style>
